import { useState, useEffect, useRef, useCallback } from "react";
import { DASHBOARD_DOMAINS, SECURITY_SENSOR_CLASSES, entityDomain, WS_MSG } from "./haConstants";
import { haProxyJson } from "./haProxy";

/* ══ useHomeAssistant ═════════════════════════════════════
   Core hook for communicating with a Home Assistant instance.
   - REST: initial fetch of states, config (areas), services
   - WebSocket: real-time state_changed subscription
   - Auto-reconnect with exponential backoff
   - Heartbeat ping/pong                                    */

const RECONNECT_BASE = 2000;
const RECONNECT_MAX  = 30000;
const PING_INTERVAL  = 30000;
const PONG_TIMEOUT   = 10000;

/**
 * Core HA integration hook.
 *
 * @param {string|null} locId   Workspace location id (used by the backend
 *                              proxy to resolve the stored token + haUrl).
 *                              REST calls go through /api/ha-proxy so users
 *                              don't need to configure CORS in HA.
 * @param {string|null} haUrl   Base URL (e.g. https://xxx.ui.nabu.casa) —
 *                              used for the direct WebSocket connection,
 *                              which isn't subject to CORS.
 * @param {string|null} token   Decrypted HA token, held in caller's ref.
 *                              Only used for the WS auth message.
 * @param {Function?}   onAlert Called when security-relevant state changes
 *                              fire (door, motion, lock, alarm, offline).
 */
function useHomeAssistant(locId, haUrl, token, onAlert) {
  /* ── State ── */
  const [connected, setConnected]           = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [entities, setEntities]             = useState([]);
  const [areas, setAreas]                   = useState([]);
  const [scenes, setScenes]                 = useState([]);

  /* ── Refs (survive re-renders, not in state to avoid loops) ── */
  const wsRef        = useRef(null);
  const msgIdRef     = useRef(1);
  const reconnectRef = useRef(0);  /* current backoff delay */
  const timerRef     = useRef(null);
  const pingRef      = useRef(null);
  const pongRef      = useRef(null);
  const mountedRef   = useRef(true);
  const pendingRef   = useRef({});  /* msgId → {resolve, reject} for callService */
  const entityMapRef = useRef({});  /* entity_id → entity (for fast WS patch) */
  const onAlertRef   = useRef(onAlert);
  onAlertRef.current = onAlert;

  /* ── REST helper ──
     Routes through /api/ha-proxy so we don't trip on HA's
     http.cors_allowed_origins. Requires locId to resolve stored
     creds; WS below uses the in-memory haUrl + token directly. */
  const fetchRest = useCallback(async (path) => {
    return haProxyJson({ locId, path });
  }, [locId]);

  /* ── Fetch initial data via REST ── */
  const fetchInitialData = useCallback(async () => {
    try {
      const [statesData] = await Promise.all([
        fetchRest("/api/states"),
        fetchRest("/api/config"), /* validates connection; area data comes from WS registry */
      ]);

      /* Build entity map — filter to dashboard-relevant domains */
      const map = {};
      const entityList = [];
      for (const s of statesData) {
        const domain = entityDomain(s.entity_id);
        if (!DASHBOARD_DOMAINS.has(domain)) continue;
        const entity = {
          entity_id: s.entity_id,
          domain,
          state: s.state,
          attributes: s.attributes || {},
          friendly_name: s.attributes?.friendly_name || s.entity_id,
          area_id: null, /* will be resolved from area registry via WS */
          last_changed: s.last_changed,
        };
        map[s.entity_id] = entity;
        entityList.push(entity);
      }
      entityMapRef.current = map;

      /* Extract areas from config (HA config has areas as an array if available) */
      /* Areas are resolved more reliably from the WS area registry — but config
         gives us something to start with until WS enriches it. */

      if (mountedRef.current) {
        setEntities(entityList);
        /* Scenes are entities in the scene domain */
        setScenes(statesData.filter(s => entityDomain(s.entity_id) === "scene").map(s => ({
          entity_id: s.entity_id,
          friendly_name: s.attributes?.friendly_name || s.entity_id,
          icon: s.attributes?.icon,
        })));
      }
      return true;
    } catch (err) {
      if (mountedRef.current) setConnectionError(err.message);
      return false;
    }
  }, [fetchRest]);

  /* ── WebSocket ── */
  const nextId = useCallback(() => msgIdRef.current++, []);

  const wsSend = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  /* Start heartbeat */
  const startPing = useCallback(() => {
    clearInterval(pingRef.current);
    clearTimeout(pongRef.current);
    pingRef.current = setInterval(() => {
      const id = nextId();
      wsSend({ id, type: "ping" });
      /* If no pong within timeout, force reconnect */
      pongRef.current = setTimeout(() => {
        const ws = wsRef.current;
        if (ws) ws.close(4001, "Pong timeout");
      }, PONG_TIMEOUT);
    }, PING_INTERVAL);
  }, [nextId, wsSend]);

  const stopPing = useCallback(() => {
    clearInterval(pingRef.current);
    clearTimeout(pongRef.current);
  }, []);

  /* Request area registry via WS (enriches entities with area_id) */
  const requestAreaRegistry = useCallback(() => {
    const id = nextId();
    wsSend({ id, type: "config/area_registry/list" });
    pendingRef.current[id] = {
      resolve: (result) => {
        if (!mountedRef.current) return;
        const areaList = (result || []).map(a => ({
          area_id: a.area_id,
          name: a.name,
          icon: a.icon,
          aliases: a.aliases || [],
        }));
        setAreas(areaList);
      },
      reject: () => {},
    };
  }, [nextId, wsSend]);

  /* Request entity registry via WS (maps entity_id → area_id) */
  const requestEntityRegistry = useCallback(() => {
    const id = nextId();
    wsSend({ id, type: "config/entity_registry/list" });
    pendingRef.current[id] = {
      resolve: (result) => {
        if (!mountedRef.current) return;
        const map = entityMapRef.current;
        let changed = false;
        for (const reg of (result || [])) {
          const ent = map[reg.entity_id];
          if (ent && reg.area_id) {
            ent.area_id = reg.area_id;
            changed = true;
          }
        }
        /* Also resolve area from device registry — entities inherit device area */
        if (changed) {
          setEntities(Object.values(map));
        }
        /* Now fetch device registry to fill in remaining area_ids */
        requestDeviceRegistry(result);
      },
      reject: () => {},
    };
  }, [nextId, wsSend]);

  /* Request device registry (entities without direct area_id inherit from their device) */
  const requestDeviceRegistry = useCallback((entityRegistry) => {
    const id = nextId();
    wsSend({ id, type: "config/device_registry/list" });
    pendingRef.current[id] = {
      resolve: (devices) => {
        if (!mountedRef.current) return;
        const devMap = {};
        for (const d of (devices || [])) {
          if (d.area_id) devMap[d.id] = d.area_id;
        }
        const map = entityMapRef.current;
        let changed = false;
        for (const reg of (entityRegistry || [])) {
          const ent = map[reg.entity_id];
          if (ent && !ent.area_id && reg.device_id && devMap[reg.device_id]) {
            ent.area_id = devMap[reg.device_id];
            changed = true;
          }
        }
        if (changed) setEntities(Object.values(map));
      },
      reject: () => {},
    };
  }, [nextId, wsSend]);

  /* Subscribe to state_changed events */
  const subscribeStateChanged = useCallback(() => {
    const id = nextId();
    wsSend({ id, type: "subscribe_events", event_type: "state_changed" });
  }, [nextId, wsSend]);

  /* Handle incoming WS message */
  const handleMessage = useCallback((msg) => {
    switch (msg.type) {
      case WS_MSG.AUTH_REQUIRED:
        wsSend({ type: "auth", access_token: token });
        break;

      case WS_MSG.AUTH_OK:
        if (mountedRef.current) {
          setConnected(true);
          setConnectionError(null);
        }
        reconnectRef.current = 0;
        startPing();
        /* Request registries to enrich entities with area info */
        requestAreaRegistry();
        requestEntityRegistry();
        subscribeStateChanged();
        break;

      case WS_MSG.AUTH_INVALID:
        if (mountedRef.current) {
          setConnected(false);
          setConnectionError("Invalid HA token — re-enter credentials.");
        }
        break;

      case WS_MSG.PONG:
        clearTimeout(pongRef.current);
        break;

      case WS_MSG.RESULT: {
        const pending = pendingRef.current[msg.id];
        if (pending) {
          delete pendingRef.current[msg.id];
          if (msg.success) pending.resolve(msg.result);
          else pending.reject(msg.error);
        }
        break;
      }

      case WS_MSG.EVENT: {
        const evtData = msg.event?.data;
        if (!evtData?.new_state) break;
        const eid = evtData.entity_id;
        const domain = entityDomain(eid);
        if (!DASHBOARD_DOMAINS.has(domain)) break;

        const map = entityMapRef.current;
        const existing = map[eid];
        const updated = {
          entity_id: eid,
          domain,
          state: evtData.new_state.state,
          attributes: evtData.new_state.attributes || {},
          friendly_name: evtData.new_state.attributes?.friendly_name || eid,
          area_id: existing?.area_id || null,
          last_changed: evtData.new_state.last_changed,
        };
        map[eid] = updated;
        if (mountedRef.current) setEntities(Object.values(map));

        /* ── Alert detection for notifications ── */
        const oldState = evtData.old_state?.state;
        const newState = evtData.new_state.state;
        if (oldState && oldState !== newState && onAlertRef.current) {
          const attrs = evtData.new_state.attributes || {};
          const devClass = attrs.device_class || "";
          let alertKey = null;

          if (domain === "alarm_control_panel") {
            alertKey = newState === "triggered" ? "sh_alarm_triggered" : "sh_alarm_armed";
          } else if (domain === "lock") {
            alertKey = "sh_lock_changed";
          } else if (domain === "binary_sensor" && SECURITY_SENSOR_CLASSES.has(devClass)) {
            if (newState === "on") {
              alertKey = devClass === "motion" ? "sh_motion_detected" : "sh_door_opened";
            }
          } else if (newState === "unavailable" && oldState !== "unavailable") {
            alertKey = "sh_device_offline";
          }

          if (alertKey) {
            onAlertRef.current({
              eventKey: alertKey,
              entityId: eid,
              entityName: updated.friendly_name,
              area: updated.area_id || "",
              oldState,
              newState,
              domain,
            });
          }
        }
        break;
      }

      default:
        break;
    }
  }, [token, wsSend, startPing, requestAreaRegistry, requestEntityRegistry, subscribeStateChanged]);

  /* ── Connect WebSocket ── */
  const connectWs = useCallback(() => {
    if (!haUrl || !token) return;
    /* Determine ws:// or wss:// from haUrl */
    const wsUrl = haUrl.replace(/^http/, "ws") + "/api/websocket";

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          handleMessage(msg);
        } catch { /* ignore malformed JSON */ }
      };

      ws.onclose = () => {
        stopPing();
        if (mountedRef.current) {
          setConnected(false);
          /* Exponential backoff reconnect */
          const delay = Math.min(
            reconnectRef.current === 0 ? RECONNECT_BASE : reconnectRef.current * 2,
            RECONNECT_MAX
          );
          reconnectRef.current = delay;
          timerRef.current = setTimeout(() => {
            if (mountedRef.current) connectWs();
          }, delay);
        }
      };

      ws.onerror = () => {
        /* onerror is always followed by onclose — no need to reconnect here */
      };
    } catch (err) {
      if (mountedRef.current) setConnectionError(err.message);
    }
  }, [haUrl, token, handleMessage, stopPing]);

  /* ── Init: fetch REST data then open WS ── */
  useEffect(() => {
    mountedRef.current = true;
    if (!haUrl || !token) return;

    let cancelled = false;

    (async () => {
      const ok = await fetchInitialData();
      if (cancelled) return;
      if (ok) connectWs();
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      /* Cleanup WS */
      stopPing();
      clearTimeout(timerRef.current);
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null; /* prevent reconnect on intentional close */
        ws.close();
        wsRef.current = null;
      }
      pendingRef.current = {};
    };
  }, [haUrl, token, fetchInitialData, connectWs, stopPing]);

  /* ── Capacitor / visibility: disconnect on background, reconnect on foreground ── */
  useEffect(() => {
    if (!haUrl || !token) return;
    function handleVisibility() {
      if (document.hidden) {
        /* Background — disconnect to save battery */
        stopPing();
        clearTimeout(timerRef.current);
        const ws = wsRef.current;
        if (ws) { ws.onclose = null; ws.close(); wsRef.current = null; }
        if (mountedRef.current) setConnected(false);
      } else {
        /* Foreground — reconnect */
        reconnectRef.current = 0;
        connectWs();
      }
    }
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [haUrl, token, connectWs, stopPing]);

  /* ── Public: call a service ── */
  const callService = useCallback((domain, service, entityId, data = {}) => {
    return new Promise((resolve, reject) => {
      const id = nextId();
      const msg = {
        id,
        type: "call_service",
        domain,
        service,
        target: entityId ? { entity_id: entityId } : undefined,
        service_data: data,
      };
      pendingRef.current[id] = { resolve, reject };
      wsSend(msg);
      /* Timeout after 15s */
      setTimeout(() => {
        if (pendingRef.current[id]) {
          delete pendingRef.current[id];
          reject(new Error("Service call timed out"));
        }
      }, 15000);
    });
  }, [nextId, wsSend]);

  /* ── Public: trigger a scene ── */
  const triggerScene = useCallback((sceneEntityId) => {
    return callService("scene", "turn_on", sceneEntityId);
  }, [callService]);

  /* ── Public: disconnect ── */
  const disconnect = useCallback(() => {
    stopPing();
    clearTimeout(timerRef.current);
    reconnectRef.current = 0;
    const ws = wsRef.current;
    if (ws) {
      ws.onclose = null;
      ws.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, [stopPing]);

  return {
    connected,
    connectionError,
    entities,
    areas,
    scenes,
    callService,
    triggerScene,
    disconnect,
  };
}

export default useHomeAssistant;
